import { Alert, Button, Card, Dropdown, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import useIsMobile from '../hooks/useIsMobile';

// A column-spec-driven list that renders a normal `<Table>` on desktop and a stacked-card list
// on phones (< 768px) - the dashboards' wide admin/owner tables were unusable on a narrow screen.
// One definition drives both layouts so they can't drift apart.
//
//   <ResponsiveTable
//     rows={paged.rows}
//     rowKey={(r) => r._id}
//     empty="No listings here."
//     rowClassName={(r) => (r.status === 'disabled' ? 'text-muted' : '')}
//     columns={[
//       { key: 'name',   header: 'Item',   cell: (r) => <>{r.name}</>,   card: 'title' },
//       { key: 'owner',  header: 'Owner',  cell: (r) => r.ownerName,     card: 'subtitle' },
//       { key: 'posted', header: 'Posted', cell: (r) => date(r),         hideBelow: 'md' },
//       { key: 'status', header: 'Status', cell: (r) => <Badge/>,        card: 'badge' },
//     ]}
//     actions={(r) => [
//       { label: 'Details', onClick: () => open(r), variant: 'link' },
//       { label: 'Edit', href: `/admin/items/${r._id}/edit` },
//       { label: 'Disable', onClick: () => toggle(r), variant: 'danger' },
//     ]}
//   />
//
// Column `card` roles: 'title' | 'subtitle' | 'badge' | 'hidden' (mobile-only positioning);
// anything else becomes a label/value row in the card. `hideBelow: 'md' | 'lg'` hides the column
// on smaller *desktop* widths only (unchanged `d-none d-*-table-cell` behaviour) - on a phone it
// still shows as a card row unless `card: 'hidden'`.
//
// `actions` may return an array of `{ label, onClick?, href?, variant?, disabled?, hidden? }`
// (rendered as inline buttons on desktop, a `⋯` menu on mobile) OR a ready React node (e.g. the
// shared <BookingActions/> cluster), used as-is in both layouts.
//
// `cardsBelow` picks the width at which the table flips to the card list: 'md' (default, 768px),
// 'lg' (992px) or 'xl' (1200px). Use a wider one for a table with only a few but very verbose
// columns (a contact block, a multi-line status panel) that can't fit a laptop-width container
// without crushing a column to one word per line.

const HIDE_CLASS = { md: 'd-none d-md-table-cell', lg: 'd-none d-lg-table-cell' };
const CARDS_BELOW = { md: 767.98, lg: 991.98, xl: 1199.98 };
const cx = (...xs) => xs.filter(Boolean).join(' ');
const keyOf = (rowKey, row, i) =>
  (typeof rowKey === 'function' ? rowKey(row) : row?.[rowKey]) ?? row?._id ?? i;

function DesktopActions({ items }) {
  return (
    <>
      {items.map((a, i) => {
        if (a.href) {
          return (
            <Button key={i} as={Link} to={a.href} size="sm" variant={a.variant || 'outline-secondary'} className="me-2 mb-1">
              {a.label}
            </Button>
          );
        }
        const variant = a.variant === 'danger' ? 'outline-danger'
          : a.variant === 'success' ? 'outline-success'
          : a.variant || 'link';
        return (
          <Button
            key={i}
            size="sm"
            variant={variant}
            className={variant === 'link' ? 'p-0 me-2 align-baseline' : 'me-2 mb-1'}
            onClick={a.onClick}
            disabled={a.disabled}
          >
            {a.label}
          </Button>
        );
      })}
    </>
  );
}

function MobileActions({ items }) {
  if (items.length <= 2) {
    return (
      <div className="d-flex flex-wrap gap-2">
        <DesktopActions items={items} />
      </div>
    );
  }
  return (
    <Dropdown align="end">
      <Dropdown.Toggle size="sm" variant="outline-secondary" className="rt-card__menu">
        <span aria-hidden="true">⋯</span>
        <span className="visually-hidden">Actions</span>
      </Dropdown.Toggle>
      <Dropdown.Menu renderOnMount popperConfig={{ strategy: 'fixed' }}>
        {items.map((a, i) => (a.href ? (
          <Dropdown.Item key={i} as={Link} to={a.href}>{a.label}</Dropdown.Item>
        ) : (
          <Dropdown.Item
            key={i}
            onClick={a.onClick}
            disabled={a.disabled}
            className={a.variant === 'danger' ? 'text-danger' : undefined}
          >
            {a.label}
          </Dropdown.Item>
        )))}
      </Dropdown.Menu>
    </Dropdown>
  );
}

function renderActions(result, Layout) {
  if (result == null) return null;
  if (Array.isArray(result)) {
    const items = result.filter((a) => a && !a.hidden);
    return items.length ? <Layout items={items} /> : null;
  }
  return result; // a ready node (e.g. <BookingActions/>)
}

export default function ResponsiveTable({
  rows = [],
  columns = [],
  actions,
  rowKey = '_id',
  rowClassName,
  empty = 'Nothing here.',
  size,
  className,
  cardsBelow = 'md'
}) {
  const isMobile = useIsMobile(CARDS_BELOW[cardsBelow] ?? CARDS_BELOW.md);

  if (isMobile) {
    const titleCol = columns.find((c) => c.card === 'title');
    const subtitleCol = columns.find((c) => c.card === 'subtitle');
    const badgeCol = columns.find((c) => c.card === 'badge');
    const rowCols = columns.filter((c) => !['title', 'subtitle', 'badge', 'hidden'].includes(c.card));

    if (rows.length === 0) return <Alert variant="light" className="mb-0">{empty}</Alert>;

    return (
      <div className={cx('rt-cards', className)}>
        {rows.map((row, i) => {
          const act = renderActions(actions?.(row), MobileActions);
          return (
            <Card key={keyOf(rowKey, row, i)} className={cx('rt-card', rowClassName && rowClassName(row))}>
              <Card.Body>
                <div className="rt-card__head">
                  <div className="rt-card__headmain">
                    {titleCol && <div className="rt-card__title">{titleCol.cell(row)}</div>}
                    {subtitleCol && <div className="rt-card__subtitle">{subtitleCol.cell(row)}</div>}
                  </div>
                  {badgeCol && <div className="rt-card__badge">{badgeCol.cell(row)}</div>}
                </div>

                {rowCols.length > 0 && (
                  <dl className="rt-card__dl">
                    {rowCols.map((c) => {
                      const v = c.cell(row);
                      const emptyish = v == null || v === '' || v === false;
                      if (emptyish && c.hideEmpty) return null;
                      return (
                        <div className="rt-card__row" key={c.key}>
                          <dt>{c.header}</dt>
                          <dd>{emptyish ? '—' : v}</dd>
                        </div>
                      );
                    })}
                  </dl>
                )}

                {act && <div className="rt-card__actions">{act}</div>}
              </Card.Body>
            </Card>
          );
        })}
      </div>
    );
  }

  const colCount = columns.length + (actions ? 1 : 0);
  // A wide, verbose table (name + email + phone in one cell, a multi-line status cell, an action
  // button stack) can't fit a narrow-ish window. Without a floor the browser keeps shrinking
  // columns until their text wraps one word per line - "vertical" text that spills its row on
  // Edge and on phones. Give the table a min-width that scales with the column count so
  // `.table-responsive` scrolls horizontally instead; a slim 2-4 column list still gets no
  // scrollbar it doesn't need.
  const minWidth = colCount > 4 ? `${colCount * 10}rem` : undefined;

  return (
    <Table
      responsive
      hover
      size={size}
      className={className}
      style={minWidth ? { minWidth } : undefined}
    >
      <thead>
        <tr>
          {columns.map((c) => (
            <th key={c.key} className={cx(c.thClassName, HIDE_CLASS[c.hideBelow])}>{c.header}</th>
          ))}
          {actions && <th className="text-end" aria-label="Actions" />}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && (
          <tr><td colSpan={colCount}><span className="text-muted">{empty}</span></td></tr>
        )}
        {rows.map((row, i) => {
          // `text-nowrap` keeps a button row on one line, but a ready node (e.g. <BookingActions/>)
          // brings its own multi-line layout and captions - forcing nowrap on those overflows the
          // cell. Only the array (button) form gets it.
          const actionResult = actions ? actions(row) : null;
          return (
            <tr key={keyOf(rowKey, row, i)} className={rowClassName ? rowClassName(row) || undefined : undefined}>
              {columns.map((c) => (
                <td key={c.key} className={cx(c.className, HIDE_CLASS[c.hideBelow])}>{c.cell(row)}</td>
              ))}
              {actions && (
                <td className={cx('text-end rt-actions-cell', Array.isArray(actionResult) && 'text-nowrap')}>
                  {renderActions(actionResult, DesktopActions)}
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
}
